import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  MenuBook as StoryIcon,
  EmojiEvents as EpicIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EnhancedAISprintSuggestion = ({
  backlogIssues,
  projectId,
  onApplySuggestion,
  projectKey,
  sprints
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [sprintConfig, setSprintConfig] = useState({});

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  const fetchAISuggestions = async () => {
    if (!backlogIssues || backlogIssues.length === 0) {
      setError('No backlog issues available for analysis');
      return;
    }

    setLoading(true);
    setError(null);

    const company_id = getCompanyIdFromToken();
    if (!company_id) {
      setError('Authentication error. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const backlogIssuesForAI = backlogIssues.filter(
        (issue) =>
          issue.status === 1 ||
          issue.status === 2 ||
          issue.sprintId === null ||
          issue.sprintId === undefined
      );

      if (backlogIssuesForAI.length === 0) {
        setError('No suitable backlog issues found for sprint planning');
        setLoading(false);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/ai/sprint/sprint-suggestions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
        
                Authorization: `Bearer ${token}`,
             
          
           },
          body: JSON.stringify({
            issues: backlogIssuesForAI,
            projectId,
            projectKey,
            existingSprints: sprints
          })
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        setSuggestions(data.data);
        // Initialize config with AI suggestions
        setSprintConfig({
          name: data.data.sprintName,
          goal: data.data.sprintGoal,
          duration: parseInt(data.data.sprintDuration) || 2,
          issues: data.data.recommendedIssues
        });
      } else {
        if (data.code === 'AI_PLAN_UPGRADE_REQUIRED') {
          setError({
            message: data.message || 'AI features are not included in your current plan. Please upgrade to unlock AI.',
            severity: 'warning',
            upgrade_required: true
          });
          window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Sprint Planning" } }));
        } else if (data.code === 'AI_TOKENS_EXHAUSTED') {
          setError({
            message: `⚠️ ${data.message || 'You have used all your AI tokens for this billing cycle.'}`,
            severity: 'warning'
          });
        } else {
          setError(data.message || 'Failed to get AI suggestions');
        }
      }
    } catch (err) {
      setError('Failed to connect to AI service');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandClick = () => {
    if (!expanded && !suggestions) fetchAISuggestions();
    setExpanded(!expanded);
  };

  const handleOpenConfig = () => {
    setConfigDialogOpen(true);
  };

  const handleCloseConfig = () => {
    setConfigDialogOpen(false);
  };

  const handleConfigChange = (field, value) => {
    setSprintConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateSprint = () => {
    if (onApplySuggestion && sprintConfig.issues?.length > 0) {
      onApplySuggestion(
        sprintConfig.issues.map(issue => issue.id),
        sprintConfig
      );
      setConfigDialogOpen(false);
    }
  };

  const getIssueTypeInfo = (typeId) => {
    const types = [
      { id: 1, name: 'Bug', icon: <BugIcon />, color: '#ff8a50' },
      { id: 2, name: 'Epic', icon: <EpicIcon />, color: '#9c27b0' },
      { id: 3, name: 'Story', icon: <StoryIcon />, color: '#2196f3' },
      { id: 4, name: 'Task', icon: <TaskIcon />, color: '#4caf50' }
    ];
    return types.find(t => t.id === typeId) || types[3];
  };

  const getPriorityInfo = (priorityId) => {
    const priorities = [
      { id: 1, name: 'Highest', color: '#e53e3e' },
      { id: 2, name: 'High', color: '#ed8936' },
      { id: 3, name: 'Medium', color: '#d69e2e' },
      { id: 4, name: 'Low', color: '#38a169' },
      { id: 5, name: 'Lowest', color: '#718096' }
    ];
    return priorities.find(p => p.id === priorityId) || priorities[2];
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: '3px',
          boxShadow: '0 1px 1px rgba(9, 30, 66, 0.25)',
          borderColor: '#E0E0E0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            borderBottom: expanded ? '1px solid #DFE1E6' : 'none',
            bgcolor: 'transparent',
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'white'
          }}
          onClick={handleExpandClick}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <IconButton size="small" sx={{ p: 0.5, mr: 1, color: 'white' }}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s'
                }}
              />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon sx={{ color: '#FFD700' }} />
              <Typography variant="subtitle1" fontWeight="600">
                AI Sprint Planning 
              </Typography>
              <Tooltip title="AI analyzes backlog to suggest optimal sprint content, duration, and goals">
                <InfoIcon
                  fontSize="small"
                  sx={{ color: 'rgba(255,255,255,0.8)' }}
                />
              </Tooltip>
            </Box>
          </Box>

          {suggestions && (
            <Chip
              label={`${suggestions.recommendedIssues?.length || 0} issues · ${suggestions.sprintDuration}`}
              size="small"
              sx={{
                height: 20,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: 500,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
          )}
        </Box>

        {/* Body */}
        <Collapse in={expanded}>
          <Box sx={{ bgcolor: 'white', p: 2 }}>
            {loading && (
              <Box textAlign="center" py={3}>
                <CircularProgress size={32} sx={{ color: '#667eea' }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  AI is analyzing {backlogIssues.length} backlog issues...
                </Typography>
              </Box>
            )}

            {error && (
              <Alert 
                severity={error.severity || (typeof error === 'string' && error.startsWith('⚠️') ? 'warning' : 'error')}
                sx={{ mb: 2, borderRadius: '8px', alignItems: 'center' }}
                action={
                  error?.upgrade_required && (
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Sprint Planning" } }))}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Upgrade Plan
                    </Button>
                  )
                }
              >
                {error.message || error}
              </Alert>
            )}

            {suggestions && (
              <>
                {/* AI Recommendation Alert */}
                <Alert 
                  severity="info" 
                  sx={{ mb: 3 }}
                  icon={<AutoAwesomeIcon />}
                >
                  <Typography variant="body2">
                    <strong>AI Recommendation:</strong> Generated through AI analysis, this sprint plan offers a data-driven recommendation based on the {backlogIssues.length} issues currently in your backlog. While optimized for efficiency, it remains an advisory suggestion and should be evaluated as part of your team’s planning process.
                   
                  </Typography>
                </Alert>

                {/* Sprint Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {suggestions.recommendedIssues?.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Issues
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {suggestions.totalStoryPoints}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Story Points
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {suggestions.sprintDuration}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Duration
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {suggestions.priorityFocus}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Focus
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Sprint Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="600" color="#667eea" gutterBottom>
                    {suggestions.sprintName}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Goal:</strong> {suggestions.sprintGoal}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Analysis:</strong> {suggestions.analysisSummary}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Recommended Issues */}
                <Typography variant="h6" gutterBottom>
                  Recommended Issues
                </Typography>
                <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                  {suggestions.recommendedIssues?.map((issue, index) => {
                    const typeInfo = getIssueTypeInfo(issue.issueType);
                    const priorityInfo = getPriorityInfo(issue.priority);
                    return (
                      <ListItem
                        key={issue.id}
                        sx={{
                          border: '1px solid #F0F0F0',
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: '#fafafa'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: '#667eea',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.75rem'
                            }}
                          >
                            {index + 1}
                          </Box>
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {typeInfo.icon}
                              <Typography variant="body2" fontWeight="500">
                                <strong>{issue.key}:</strong> {issue.summary}
                              </Typography>
                              <Chip
                                label={`${issue.storyPoints || 0} pts`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                              <Chip
                                label={priorityInfo.name}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: priorityInfo.color,
                                  color: 'white'
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {issue.aiReason}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleOpenConfig}
                  >
                    Customize Sprint
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleCreateSprint}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    Create Sprint
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Sprint Configuration Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={handleCloseConfig}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">Customize Sprint</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              These are AI-generated recommendations. Feel free to adjust the sprint details based on your team's specific needs and context.
            </Typography>
          </Alert>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sprint Name"
                value={sprintConfig.name || ''}
                onChange={(e) => handleConfigChange('name', e.target.value)}
                helperText="Name your sprint"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Sprint Goal"
                value={sprintConfig.goal || ''}
                onChange={(e) => handleConfigChange('goal', e.target.value)}
                helperText="What do you want to achieve in this sprint?"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sprint Duration</InputLabel>
                <Select
                  value={sprintConfig.duration || 2}
                  label="Sprint Duration"
                  onChange={(e) => handleConfigChange('duration', e.target.value)}
                >
                  <MenuItem value={1}>1 week</MenuItem>
                  <MenuItem value={2}>2 weeks</MenuItem>
                  <MenuItem value={3}>3 weeks</MenuItem>
                  <MenuItem value={4}>4 weeks</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Velocity"
                type="number"
                value={suggestions?.totalStoryPoints || 0}
                InputProps={{ readOnly: true }}
                helperText="Based on selected issues"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Issues: {sprintConfig.issues?.length || 0} issues
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                {sprintConfig.issues?.map((issue, index) => (
                  <Typography key={issue.id} variant="body2" sx={{ py: 0.5 }}>
                    {index + 1}. {issue.key}: {issue.summary} ({issue.storyPoints || 0} pts)
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseConfig}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSprint}
            startIcon={<PlayArrowIcon />}
          >
            Create Sprint
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnhancedAISprintSuggestion;